import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  OnInit,
  Type,
  InjectionToken,
  AbstractType,
  Injector,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { NG_VALUE_ACCESSOR, ValidationErrors } from '@angular/forms';
import { lazyInject } from '@app/shared/decorators';
import { I18n } from '@app/i18n/strings-keys';
import { E2E_ATTRIBUTES } from '@app/shared/types/enums/e2e-attributes.enum';
import { get, gt, isFunction, size } from 'lodash';

import { ValueAccessorService } from '../accessors/index';
import { I18nService } from '@app/shared/services/i18n/i18n.service';

enum VALIDATION_ERROR {
  REQUIRED = 'required',
  MIN_LENGTH = 'minlength',
  MAX_LENGTH = 'maxlength',
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'input-component',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    ValueAccessorService,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: ValueAccessorService,
      multi: true,
    },
  ],
  standalone: false,
})
export class InputComponent implements OnInit, OnChanges {
  @Input() public label = '';
  @Input() public className: string | string[];
  @Input() public errors: ValidationErrors;
  @Input() public iconType: string;
  @Input() public iconTypeRight: string;
  @Input() public clearIcon: string;
  @Input() public rowLayout = false;
  @Input() public name: string;
  @Input() public required = false;
  @Input() public readonly = false;
  @Input() public fieldType = 'text';
  @Input() public reFocus: string = null;

  @Input()
  public set value(value: string) {
    this._value = value ?? '';
    this.el.nativeElement.value = this.value;
  }

  @Input() public placeholder = '';
  @Input() public disabled = false;
  @Input() public maxLength: number;
  @Input() public hasError = false;
  @Input() public errorMsg = '';
  @Input() public isUpperCased = false;
  @Input() public prohibitFocus = false;

  @Output() public onChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() public onInput: EventEmitter<string> = new EventEmitter<string>();
  @Output() public onFocus: EventEmitter<void> = new EventEmitter<void>();
  @Output() public onClearOut: EventEmitter<void> = new EventEmitter<void>();

  @Output() public onFocusOut: EventEmitter<string> = new EventEmitter<string>();

  public e2eAttrs = E2E_ATTRIBUTES;
  public pattern: string | null = null;

  @ViewChild('inputElement', { static: true }) public el: ElementRef;

  private _value = '';

  public get value(): string {
    return this._value;
  }

  public get isClearBtnShown(): boolean {
    return gt(size(this.el?.nativeElement?.value ?? this.value), 0);
  }

  public constructor(protected injector: Injector) {}

  @lazyInject(ValueAccessorService)
  public inputAccessor: ValueAccessorService;

  @lazyInject(I18nService)
  public i18nService: I18nService;

  public inject<T>(token: Type<T> | InjectionToken<T> | AbstractType<T>, notFoundValue?: T): T {
    if (isFunction(this.injector.get)) {
      return this.injector?.get(token, notFoundValue);
    }

    return notFoundValue;
  }

  public ngOnInit(): void {
    this.placeholder = this.placeholder || '';
    this.value = this.value || '';
    this.label = this.label || '';
    this.pattern = this.name === 'zip_code' ? '^[\\w\\d\\s-]+$' : null;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (get(changes, 'reFocus.currentValue')) {
      this.el.nativeElement.focus();
    }
  }

  public onInputHandler($event: Event): void {
    let value: string = (<HTMLInputElement>$event.target).value;

    if (this.isUpperCased) {
      value = value.toUpperCase();
    }

    this.onChangeHandler(value);
    this.onInput.emit(value);
  }

  public onFocusHandler() {
    if (this.prohibitFocus) {
      this.el.nativeElement.blur();
    }

    this.onFocus.emit();
  }

  public onFocusOutHandler($event: Event) {
    const value: string = (<HTMLInputElement>$event.target).value;

    this.onFocusOut.emit(value);
  }

  public onChangeHandler($event: string): void {
    const value: string = $event;

    this.inputAccessor.set(value);

    this.onChange.emit(value);
  }

  public onBlurHandler(): void {
    this.inputAccessor.onBlur();
  }

  public onClearClick(): void {
    this.onClearOut.emit();
    this.onChangeHandler('');
    this.onBlurHandler();
    this.value = '';
  }

  public get errorText(): string {
    switch (Object.keys(this.errors)[0]) {
      case VALIDATION_ERROR.REQUIRED:
        return this.i18nService.instant(I18n.CommonErrors.FieldIsRequired, {
          field: this.name,
        });
      case VALIDATION_ERROR.MIN_LENGTH:
        return this.i18nService.instant(I18n.CommonErrors.LengthMoreThen, {
          field: this.name,
          length: this.errors[VALIDATION_ERROR.MIN_LENGTH].requiredLength,
        });
      case VALIDATION_ERROR.MAX_LENGTH:
        return this.i18nService.instant(I18n.CommonErrors.LengthLessThen, {
          field: this.name,
          length: this.errors[VALIDATION_ERROR.MAX_LENGTH].requiredLength,
        });
      default:
        return '';
    }
  }
}
